import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';

interface IFindOrCreateAndUpdateBlogDesignParams {
	onError?: (err: Api.IOperationResultNoValue) => void;
	onSuccess?: (val: Api.CustomBlogStyles) => void;
}

interface IFindOrCreateAndUpdateBlogDesignRequest {
	id: string;
	style: Api.FooterCustomization | Api.HeaderCustomization | Api.BlogHomeCustomization | Api.BlogPostCustomization;
	impersonationContext?: Api.IImpersonationContext;
}

// Map type to class name for polymorphic deserialization
const typeToClassName: Partial<Record<Api.CustomPageType, string>> = {
	[Api.CustomPageType.Header]: 'HeaderCustomization',
	[Api.CustomPageType.Footer]: 'FooterCustomization',
	[Api.CustomPageType.BlogHome]: 'BlogHomeCustomization',
	[Api.CustomPageType.BlogPost]: 'BlogPostCustomization',
};

export function useFindOrCreateAndUpdateBlogStyleMutation({
	onError,
	onSuccess,
}: IFindOrCreateAndUpdateBlogDesignParams = {}) {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: async ({ id: _id, style, impersonationContext }: IFindOrCreateAndUpdateBlogDesignRequest) => {
			if ('links' in style) {
				style.links = removeEmptyLinkObjects(style.links);
				if (style.links.length === 0) {
					delete style.links;
				}
			}

			if ('menuLinks' in style) {
				style.menuLinks = removeEmptyLinkObjects(style.menuLinks);
				if (style.menuLinks.length === 0) {
					delete style.menuLinks;
				}
			}

			const value = await userSession.webServiceHelper.callAsync<Api.CustomBlogStyles>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `CustomPageStyle/${encodeURIComponent(style.type)}/v2`,
				}),
				'POST',
				{ ...style, _type: typeToClassName[style.type] }
			);
			return value;
		},
		onError,
		onSuccess,
	});
}

const removeEmptyLinkObjects = (linkObjects: Api.NavLink[]): Api.NavLink[] => {
	if (!linkObjects) {
		return [];
	}
	return linkObjects
		.filter(link => link.label || link.destination)
		.map(link => {
			if (link.children && link.children.length > 0) {
				const filteredChildren = removeEmptyLinkObjects(link.children);
				if (filteredChildren.length === 0) {
					const { children: _, ...linkWithoutChildren } = link;
					return linkWithoutChildren as Api.NavLink;
				}
				return { ...link, children: filteredChildren };
			}
			return link;
		});
};
